import { Box, InputBase, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Text from "../../../../theme/text/Text";

import Dropdown from "../../../../theme/dropout/DropOut";
import { useEffect, useState } from "react";
import ShSwitch from "../../../../theme/inputs/ShSwitch";
import Subheading from "../../../../theme/text/Subheading";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from "react-i18next";
import { useEditShipperMutation } from "../../../../../newapi/shipper/shipperSlice";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import ShValidatedInput from "../../../../theme/inputs/ShValidatedInput";
import * as yup from "yup";
import { yupRequired } from "../../../../../utils/validation";
import ShTimePicker from "../../../../theme/ShTimePicker";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { DropdownBase } from "../../../../theme/dropdowns/Dropdown";
import { countriesFromLocale } from "../../../../../utils/countries";
import { useSelector } from "react-redux";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";
import DummyPhoneNumbers from "../components/DummyPhoneNumbers";
import EMGeneralExplainerBox from "../../../ModalsPopover/explainers/EMGeneralExplainerBox";

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <ButtonGroup version={2}>
            {props.hasModifyPermission && (
            <Shbutton
                isLoading={props.isLoading}
                className={"group-btn"}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
            )}
        </ButtonGroup>
    );
};

dayjs.extend(utc);

const Pickup = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 
    const [data, setData] = useState([]);
    const language = useSelector((state) => state.settings.language);
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    const countries = countriesFromLocale(language);

    const hasModifyPermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.SHIPPER_MANAGE, UserPrivileges.SETTINGS_ALL])
    );
    
    const [shipperInfo, setShipperInfo] = useState({
        id: 0,
        standard_time: "",
        standard_time_saturday: "",
        standard_time_sunday: "",
        cutoffTime: null,
        alternativeAddress: null,
        street: null,
        house_nr: null,
        house_nr_extra: null,
        postal: null,
        city: null,
        country: 0,
        company: null,
        contactperson: null,
    });

    const [dummyNumbers, setDummyNumbers] = useState([]);

    useEffect(() => {
        if(props.data){
            let myData  = {...props.data};
            myData["standard_time"] = dayjs.utc(myData["standard_time"]);  // This keeps the time as is, parsed as-is.
            myData["standard_time_saturday"] = dayjs.utc(myData["standard_time_saturday"]);
            myData["standard_time_sunday"] = dayjs.utc(myData["standard_time_sunday"]);
            myData["cutoffTime"] = myData["cutoffTime"] ? dayjs(myData["cutoffTime"], "HH:mm") : null;
            setShipperInfo(myData);
            setDummyNumbers(props.data.configuration?.dummyNumbers ?? []);
        }
    },[props.data]);
    
    const handleChange = (key, value) => {
        setShipperInfo(prev => ({
            ...prev,
            [key]: value
        }));
      };

    const handleDummyNumberChange = (key, index, value) => {
        setDummyNumbers(prev => prev.map((dummyNumber, i) =>
            i === index ? {...dummyNumber, [key]: value} : dummyNumber
        ));
    };

    const handleDeleteDummyNumber = (index) => {
        setDummyNumbers(prev => prev.filter((dummyNumber, i) => i !== index));
    };

    const handleAddDummyNumber = () => {
        setDummyNumbers(prev => [...prev, {phone: "", countries: []}]);
    };

    const [fieldErrors, setFieldErrors] = useState({});

    const rules = {
        company: yupRequired('company', t),
        contactperson: yupRequired('contactperson', t),
    };
    
    const schema = yup.object().shape(rules);
    
    const [EditShipper,{isLoading}] = useEditShipperMutation();
    const handleSaveClick = async () => {
        try {
            if (isLoading) return;

            const updateObject = {
                id: shipperInfo.id,
                standard_time: shipperInfo.standard_time,
                standard_time_saturday: shipperInfo.standard_time_saturday,
                standard_time_sunday: shipperInfo.standard_time_sunday,
                cutoffTime: shipperInfo.cutoffTime && dayjs(shipperInfo.cutoffTime).isValid() ? dayjs(shipperInfo.cutoffTime).format("HH:mm") : null,
                alternativeAddress: shipperInfo.alternativeAddress,
                street: shipperInfo.street,
                house_nr: shipperInfo.house_nr,
                house_nr_extra: shipperInfo.house_nr_extra,
                postal: shipperInfo.postal,
                city: shipperInfo.city,
                country: shipperInfo.country,
                company: shipperInfo.company,
                contactperson: shipperInfo.contactperson,
                configuration: {
                    ...(shipperInfo.configuration ?? {}),
                    // A record without a number or without countries can never be matched
                    dummyNumbers: dummyNumbers.filter(dummyNumber => dummyNumber.phone && dummyNumber.countries?.length > 0)
                },
            };

            setFieldErrors({});
            await schema.validate(updateObject, { abortEarly: false });
            const data = await EditShipper(updateObject).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path]: currentError.message
                }), {});
            
                // Set the validation errors in the state
                setFieldErrors(errors);
            } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("update_failed"), t, err);
            }
        }      
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions
                    isLoading={isLoading}
                    hasModifyPermission={hasModifyPermission}
                    id={props.data?.id}
                    action={handleSaveClick}
                    // active={active}
                    // blocked={blocked}
                    // remark={remark}
                    // carrier={carrier}
                    // quarantine={quarantine}
                    // enableLinkingWhenBlocked={enableLinkingWhenBlocked}
                />,
        },false);

        return () => {
            // console.log("unmounting warehouse");
        }
    },[isLoading,hasModifyPermission,props.data?.id,handleSaveClick]);
    

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} paddingX={3}
            sx={{
                "@media screen and (min-width: 48.1em)"  :{
                    marginBottom:11
                }
            }}
        >
            <Dropdown title={t("basic")} open>
                <Box sx={{
                    display:"flex",
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        flexDirection:"column"
                    }
                }}>
                    
                    {/* Two columns */}
                    
                    <Box flex={4}
                        sx={{
                            display:"flex",
                            gap:4,
                            justifyContent:"flex-start"
                        }}
                    >
                        <Box
                            sx={{
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1
                                }
                            }}
                        >
                            <Box lineHeight={"38px"}>
                                <Text>{t("company")}</Text>
                            </Box>
                        </Box>
                        <Box>
                            <Box display="flex"
                                height="32px"
                                // maxWidth="400px"
                                borderRadius="20px"
                                backgroundColor={colors.grey[100]}
                                sx={{
                                    marginTop:0.5,
                                    marginBottom:0.5,
                                    justifyContent:"space-between",
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"column"
                                    }
                                }}
                            >
                                <ShValidatedInput
                                    name="company"
                                    value={shipperInfo?.company}
                                    onChange={handleChange}
                                    error={fieldErrors.company}
                                />
                            </Box>
                            
                        </Box>
                    </Box>

                    <Box flex={4}
                        sx={{
                            display:"flex",
                            gap:4,
                            justifyContent:"flex-start"
                        }}
                    > 
                        <Box
                            sx={{
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1,
                                    // width:1
                                }
                            }}
                        >
                            <Box lineHeight={"38px"}>
                                <Text>{t("contactperson")}</Text>
                            </Box>
                            
                        </Box>
                        <Box>
                            <Box display="flex"
                                height="32px"
                            // maxWidth="400px"
                                borderRadius="20px"
                                backgroundColor={colors.grey[100]}
                                sx={{
                                    marginTop:0.5,
                                    marginBottom:0.5,
                                    justifyContent:"space-between",
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"column"
                                    }
                                }}
                            >
                                <ShValidatedInput
                                    name="contactperson"
                                    value={shipperInfo?.contactperson}
                                    onChange={handleChange}
                                    error={fieldErrors.contactperson}
                                />
                            </Box>
                            
                        </Box>
                    </Box>
                </Box>
            </Dropdown>
            <Dropdown title={t("alternative_address")} open>
                <Box paddingX={1.5}
                    sx={{
                        // "@media screen and (max-width: 47.9375em)" : {
                        //     display:"none"
                        // }
                    }}
                >
                    {/* <Subheading variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{t("alternativeaddress_onoff")}</Subheading> */}
                    <ShSwitch
                        name="alternativeAddress"
                        checked={shipperInfo?.alternativeAddress}
                        value={shipperInfo?.alternativeAddress}
                        onChange={(e) => handleChange('alternativeAddress', e.target.checked)}
                    ></ShSwitch>
                </Box>
                {/* Details */}
                {shipperInfo?.alternativeAddress && (
                    <>
                    {shipperInfo?.warehouses?.length > 1 && (
                        <Box 
                            sx={{
                                background: colors["orange"][100],
                                color: colors["orange"][400],
                                padding:2,
                                width:1,
                                marginTop:2
                            }}
                        >
                            <Typography fontWeight={"bold"} fontSize={17}>
                                {t("shipper_alternative_address_multiple_title")}
                            </Typography>
                            <Box sx={{paddingTop:0.5}}>
                                <Typography fontSize={14}>
                                    {t("shipper_alternative_address_multiple_description")}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Box 
                        sx={{
                            display:"flex",
                            borderRadius:3,
                            overflow:"hidden",
                            marginTop:3,
                            marginBottom:1,
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            "@media screen and (max-width: 47.9375em)" : {
                                marginTop:0,
                                background:"none",
                                flexDirection:"column",
                                overflow:"unset"
                            }
                        }}
                    >
                
                    {/* Editable content */}
                    <Box padding={1.5} paddingRight={4} paddingBottom={3} flex={1}
                        sx={{
                            "@media screen and (max-width: 47.9375em)" : {
                                paddingY:0,
                                paddingRight:1.5,
                            }
                        }}
                    >
                        <Box 
                            sx={{
                                display:"flex",
                                alignItems:"center",
                                gap:3,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    gap:0.5,
                                    alignItems:"flex-start",
                                }
                            }}
                        >
                            <Subheading>{t("details")}</Subheading>
                            <Text>{shipperInfo?.deliveryName}</Text>
                        </Box>

                        <Box
                            sx={{
                                display:"flex",
                                marginTop:3,
                                gap:3,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    gap:1,
                                    alignItems:"flex-start",
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:0.5,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:80
                                        }
                                    }}
                                >
                                    <Text light>{t("street")}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <InputBase type="text" value={shipperInfo?.street} sx={{px: 2, flex:1, height:32,boxSizing:"border-box"}} 
                                        onChange={handleChange}/>
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:0.5,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:80
                                        }
                                    }}
                                >
                                    <Text light>{t("house_nr")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                        borderRadius="20px"
                                        backgroundColor={colors.grey[200]}
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <InputBase type="text" 
                                        value={shipperInfo?.house_nr}
                                            sx={{
                                                px: 2, 
                                                width:56, 
                                                height:32,
                                                boxSizing:"border-box",
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    flex:1,
                                                    width:"unset"
                                                }
                                            }}
                                            onChange={handleChange}/>
                                        <Box 
                                            sx={{
                                                marginY:0.5,
                                                height:24,
                                                width:"1px",
                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[300]
                                            }}
                                        />
                                        <InputBase type="text" value={shipperInfo?.house_nr_extra} sx={{px: 2, width:56, height:32,boxSizing:"border-box"}} onChange={handleChange}/>
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:0.5,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:80
                                        }
                                    }}
                                >
                                    <Text light>{t("postal_code")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <InputBase type="text" 
                                        value={shipperInfo?.postal}
                                            sx={{
                                                px: 2, 
                                                width:96, 
                                                height:32,
                                                boxSizing:"border-box",
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    flex:1,
                                                    width:"unset"
                                                }
                                            }}
                                            onChange={handleChange}/>
                                        {/* <Box 
                                            sx={{
                                                marginY:0.5,
                                                height:24,
                                                width:"1px",
                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[300]
                                            }}
                                        />
                                        <InputBase type="text" sx={{px: 2, width:56, height:32,boxSizing:"border-box"}} /> */}
                                    </Box>
                                </Box>
                            </Box>
                        
                            
                        </Box>


                        <Box
                            sx={{
                                display:"flex",
                                marginTop:3,
                                gap:3,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    gap:1,
                                    alignItems:"flex-start",
                                    marginTop:4
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:0.5,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:80
                                        }
                                    }}
                                >
                                    <Text light>{t("city")}</Text>
                                </Box>

                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >

                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                        }}
                                    >
                                        <InputBase type="text"
                                        value={shipperInfo?.city}
                                            sx={{
                                                px: 2, 
                                                flex:1, 
                                                height:32,
                                                boxSizing:"border-box",
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    flex:1,
                                                    width:"unset"
                                                }
                                            }}
                                            onChange={handleChange}/>
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:0.5,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"row",
                                        gap:5,
                                        alignItems:"center",
                                        width:1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:80
                                        }
                                    }}
                                >
                                    <Text light>{t("country")}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flex:1
                                        }
                                    }}
                                >
                                    <Box display="flex"
                                        borderRadius="20px"
                                        sx={{
                                            marginTop:0.5,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }
                                            , width: 235
                                        }}
                                    >
                                        {/* <InputBase type="text" sx={{px: 2, flex:1, height:32,boxSizing:"border-box"}}
                                        value={shipperInfo?.deliveryCountry} /> */}
                                        <DropdownBase
                                            key={shipperInfo?.country}
                                            name="country"
                                            displayName="name"
                                            changeField="alpha2"
                                            options={countries ?? null}
                                            noSelection={t("choose_option")} 
                                            onChange={handleChange}
                                            selected={countries?.findIndex(country => (shipperInfo.country && shipperInfo.country === country.alpha2) || (country.alpha2 === 'NL')) ?? -1}
                                            searchOptions={{
                                                enabled: true,
                                                placeholder: t("search_country_placeholder")
                                            }}
                                            closeOnSelection
                                        >
                                            <Box
                                                sx={{
                                                    flex:1,
                                                    // flexGrow:0,
                                                    display:"flex",
                                                    alignItems:"center",
                                                    justifyContent:"center",
                                                    //background:colorSettings.streamrulevalue,
                                                    height:32,
                                                    borderRadius:4,
                                                    "&:hover" : {
                                                        //background:colorSettings.streamrulevaluehover,
                                                        cursor:"pointer",
                                                    },
                                                    "@media screen and (max-width: 768px)" : {
                                                        // ml:1,
                                                    }
                                                }}
                                            >
                                                <Text noBreak>{countries.find(country => country.id === shipperInfo?.country)?.name}</Text>
                                            </Box>
                                        </DropdownBase>
                                    </Box>
                                </Box>
                            </Box>

                            
                        </Box>
                    </Box>
                
            </Box>
            </>
            )}
            </Dropdown>
            <Dropdown title={t("pickup_times")} subTitle={t("pickup_times_description")} open={window.innerWidth > 767}>
            {/* <Divider></Divider> */}
                <Box sx={{
                    display:"flex",
                    flexDirection:"column"
                }}>
                    {/* Two columns */}
                    <Box
                        sx={{
                            display:"flex",
                            // gap:1,
                            justifyContent:"flex-start",
                            flexDirection:"column",
                            gap:1.5,
                        }}
                    >
                        {/* <LocalizationProvider dateAdapter={AdapterDayjs}> */}
                            <Box 
                            sx={{
                                width:1,
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"space-between",
                                gap:7
                            }}
                        >

                            <Box
                                sx={{
                                    width:120,
                                }}
                            >
                                <Text>{t("mon_fri")}</Text>&nbsp;
                            </Box>
                            
                            <ShTimePicker
                                value={shipperInfo.standard_time}
                                onChange={(e) => {
                                    handleChange('standard_time', dayjs(e))
                                }}
                            />

                            {/* <Box    
                                sx={{
                                    flex:1,
                                    backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                    borderRadius:4,
                                    display:"flex",
                                    height:32
                                }}
                            >
                                <TimePicker btnSize={"24"} 
                                showTimeSelect showTimeSelectOnly
                                dateFormat="HH:mm"
                                timeFormat="HH:mm"
                                format="HH:mm"
                                value={data.mon_fri}
                                onChange={(e) => handleChange('standard_time', e)}/>
                            </Box> */}
                            </Box>
                            <Box 
                                sx={{
                                    width:1,
                                    display:"flex",
                                    alignItems:"center",
                                    justifyContent:"space-between",
                                    gap:7,
                                }}
                            >

                                <Box
                                    sx={{
                                        width:120,
                                    }}
                                >
                                    <Text>{t("saturday")}</Text>&nbsp;
                                </Box>
                                
                                <ShTimePicker
                                    value={shipperInfo.standard_time_saturday}
                                    // value={dayjs(data.standard_time_saturday, "HH:mm")}
                                    onChange={(e) => {
                                        handleChange('standard_time_saturday', dayjs(e))
                                    }}
                                />
                                
                            </Box>
                            <Box 
                                sx={{
                                    width:1,
                                    display:"flex",
                                    alignItems:"center",
                                    justifyContent:"space-between",
                                    gap:7
                                }}
                            >

                                <Box
                                    sx={{
                                        width:120,
                                    }}
                                >
                                    <Text>{t("sunday")}</Text>&nbsp;
                                </Box>
                                <ShTimePicker
                                    value={shipperInfo.standard_time_sunday}
                                    // value={dayjs(data.standard_time_sunday, "HH:mm")}
                                    onChange={(e) => {
                                        handleChange('standard_time_sunday', dayjs(e))
                                    }}
                                />
                                
                            </Box>
                            <Box 
                                sx={{
                                    width:1,
                                    display:"flex",
                                    alignItems:"center",
                                    justifyContent:"space-between",
                                    gap:7
                                }}
                            >

                                <Box
                                    sx={{
                                        width:120,
                                        display:"flex",
                                        alignItems:"center",
                                    }}
                                >
                                    <Text noBreak>{t("cutoff_time")}</Text>
                                    <EMGeneralExplainerBox
                                        title={t("cutoff_time")}
                                        text={isFulfilment ? t("em_cutoff_time_text") : t("em_cutoff_time_text_no_relation")}
                                    />
                                </Box>
                                <ShTimePicker
                                    clearable
                                    value={shipperInfo.cutoffTime}
                                    onChange={(e) => {
                                        handleChange('cutoffTime', dayjs(e))
                                    }}
                                />
                                
                            </Box>
                        {/* </LocalizationProvider>               */}
                    </Box>
                </Box>
            </Dropdown>
            <Dropdown title={t("dummy_phone_numbers")} subTitle={t("dummy_phone_numbers_description")} open={window.innerWidth > 767} isLast sx={{paddingBottom:"0px"}}>
                <DummyPhoneNumbers
                    dummyNumbers={dummyNumbers}
                    countries={countries ?? []}
                    onDummyNumberChange={handleDummyNumberChange}
                    onDeleteDummyNumber={handleDeleteDummyNumber}
                    onAddDummyNumber={handleAddDummyNumber}
                />
            </Dropdown>
            <SpDesktopFooter>

                <GridActions
                    isLoading={isLoading}
                    hasModifyPermission={hasModifyPermission}
                    id={props.data?.id}
                    action={handleSaveClick}
                    // active={active}
                    // blocked={blocked}
                    // remark={remark}
                    // carrier={carrier}
                    // quarantine={quarantine}
                    // enableLinkingWhenBlocked={enableLinkingWhenBlocked}
                />
                
            </SpDesktopFooter>
        </Box>
        
    );
}


export default Pickup;